import {
  VdFeatureManager,
  VdPerformanceTracking,
  VdTracking,
} from '@vend/vend.js'
import angular, { IPromise } from 'angular'
import each from 'lodash/each'
import filter from 'lodash/filter'
import map from 'lodash/map'
import moment from 'moment-timezone'
import jitterTime from 'jitter-time'

import {
  getFromStorage,
  saveToStorage,
  StartTimer,
  Timer,
} from '@vend/utilities'
import {
  PROMOTIONS,
  PROMOTIONS_ENDPOINT,
} from '../components/promotions/promotions.constant'
import { EVENTS, SyncInterruptedError } from '../constants'
import { $http, $log, $q, $window } from '../ng-compat/imports'
import { RetailerSettingsStore } from '../factories/database/retailer-settings-store'
import { EventEmitter } from '../util/event-emitter'
import { ProductsStore } from '../factories/database/products-store'
import {
  getProductCountBucket,
  getRetailerGroupForSyncMetrics,
} from '../functions/metrics'
import { RegisterManager } from './register-manager'
import { WebRegisterStatusManager } from './web-register-status-manager'
import { PosStateManager } from './pos-state-manager'

const SYNC_METRIC = 'Register Sync'
const HAS_ACTIVE_PROMOTIONS = 'VEND_HAS_ACTIVE_PROMOTIONS'

const syncTimeout = jitterTime(5)
const MANAGED_ENTITY_STORES = [
  'channels',
  'outlets',
  'registers',
  'taxes',
  'productTaxes',
  'products',
  'priceBooks',
  'paymentTypes',
  'customers',
  'customerTaxes',
  'customerGroups',
  'users',
  'quickKeyLayouts',
  'retailerSettings',
  'receiptTemplateTypes',
  'receiptSettings',
  'priceBookProducts',
]

type Events = {
  entitySync: [$TSFixMe]
  entitySyncComplete: [$TSFixMe]
  entitySyncTriggered: [$TSFixMe]
  syncComplete: [$TSFixMe]
  // Other event name patterns:
  // * `entitySync:${entityName}`
  // * `entitySyncComplete:${entityName}`
  // * `entitySyncTriggered:${entityName}`
  [key: string]: $TSFixMe[]
}

/**
 * Factory for managing the syncing of data between the server and IndexedDB.
 *
 * @class SyncManager
 * @constructor
 * @extends EventEmitter
 */
export class SyncManager extends EventEmitter<Events> {
  /**
   * Determine if the retailer has active promotions. We don't actually sync promotions.
   * Get init value from local storage if it exists, otherwise set default value as true.
   */
  public hasActivePromotions: boolean

  /**
   * Sync manager for versioned entities.
   */
  private syncedEntityVersions: $TSFixMe

  /**
   * Whether at least one sync has occurred (for this page load).
   */
  public syncHasOccurred = false

  /**
   * Promise for the current active sync, resolved once that sync has completed.
   */
  private _currentActiveSync: $TSFixMe = null

  /**
   * Associative array of entity store name to EntitySyncConfig for each of the entities currently being
   * synced in the active sync.
   */
  private _currentEntitiesBeingSynced: $TSFixMe = {}

  /**
   * Collection of entities waiting for the active outlet ID to be set before they can continue syncing.
   * Stored as a map of entity store name to a Deferred. Each Deferred is resolved with the outlet ID once it
   * is set to continue the sync of that respective entity.
   */
  private _entitiesPendingOutletId: $TSFixMe = {}

  /**
   * Deferred for the next scheduled sync.
   */
  private _nextScheduledSync: $TSFixMe = null

  /**
   * Timer for the next scheduled sync.
   */
  private _nextSyncPoll: $TSFixMe = null

  /**
   * List of entities that require resyncing when the outlet changes.
   */
  private _resyncOnOutletIdChange: $TSFixMe

  private _whenInitialised: $TSFixMe

  private _whenSyncTriggered: $TSFixMe

  constructor(
    private versionsStore: $TSFixMe,
    private posStateManager: PosStateManager,
    private VersionedEntitySyncManager: $TSFixMe,
    private webRegisterStatusManager: WebRegisterStatusManager,
    wrDebugTools: $TSFixMe,
    private registerManager: RegisterManager,
    private apiIdentification: $TSFixMe,
    private vdPerformanceTracking: VdPerformanceTracking,
    private vdTracking: VdTracking,
    private retailerSettingsStore: RetailerSettingsStore,
    private productsStore: ProductsStore,
    private readonly vdFeatureManager: VdFeatureManager
  ) {
    'ngInject'

    super()
    wrDebugTools.register('syncManager')

    this.syncedEntityVersions = new this.VersionedEntitySyncManager({
      managedEntityStores: MANAGED_ENTITY_STORES,
    })

    const hasActivePromotions = getFromStorage<boolean>(HAS_ACTIVE_PROMOTIONS)
    this.hasActivePromotions =
      hasActivePromotions === undefined ? true : hasActivePromotions
  }

  /**
   * The interval between syncs.
   *
   * @property syncTimeout
   * @type {Number}
   * @readOnly
   */
  public get syncTimeout() {
    return syncTimeout
  }

  /**
   * Initialises the SyncManager and any related sync managers, database stores etc.
   *
   * @method init
   *
   * @return {Promise} A promise resolved with the syncManager instance once initialisation is complete
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = $q
        .all([this.syncedEntityVersions.init(), this.posStateManager.init()])
        .then(() => this._init())
    }
    return this._whenInitialised
  }

  /**
   * Synchronises the data stored in IndexedDB with the data stored on the server.
   *
   * @method sync
   *
   * @param {Object} options
   *        @param {Boolean} [options.skipEmitSyncComplete]
   *               Optional boolean true when we need to skip the emit of the sync complete event, false otherwise
   *
   * @return {Promise} A promise resolved once the sync has completed
   */
  public sync(
    options: {
      skipEmitSyncComplete?: boolean
      isFirstSync?: boolean
    } = {}
  ) {
    let syncPromise

    if (this.syncInProgress()) {
      if (!this._nextScheduledSync) {
        this._currentActiveSync
          .catch(angular.noop)
          .finally(() => this._executeNextScheduledSync())
        this._nextScheduledSync = $q.defer()
      }
      syncPromise = this._nextScheduledSync.promise
    } else {
      this.cancelPoll()

      syncPromise = this._executeSync(options)
    }

    return syncPromise
  }

  /**
   * Whether a sync is currently in progress or not.
   *
   * @method syncInProgress
   *
   * @return {boolean} true if a sync is currently in progress, false otherwise
   */
  public syncInProgress() {
    return Boolean(this._currentActiveSync)
  }

  /**
   * Cancels the current regular sync poll if there is one. Call `syncManager.sync()` to restart it again.
   *
   * @method cancelPoll
   */
  public cancelPoll() {
    this._cancelNextPoll()

    if (this.syncInProgress()) {
      this._currentActiveSync
        .catch(angular.noop)
        .finally(() => this._cancelNextPoll())
    }
  }

  /**
   * Resolves when the current active sync completes or immediately if no current sync is in progress.
   *
   * @method whenCurrentSyncCompletes
   */
  public whenCurrentSyncCompletes() {
    return $q.resolve(this._currentActiveSync)
  }

  /**
   * Get notified when a complete set of data for a particular entity store is available. If we're online this
   * will also wait for any current sync of that entity to complete, otherwise simply ensures we have at least
   * done a complete sync of this entity once before.
   *
   * @method whenCompleteEntityDataAvailable
   *
   * @param entityStoreName {Stirng}
   *        The name of the entity store for the entity to wait for sync to complete
   *
   * @return {Promise} A promise resolved when a complete dataset for the specified entity is available
   */
  public whenCompleteEntityDataAvailable(
    entityStoreName: string
  ): IPromise<unknown> {
    return this._whenSyncStarted().then(() =>
      $q(resolve => {
        const resolveWhenEntitySyncCompleteFired = () => {
          this.once('entitySyncComplete:' + entityStoreName, () => resolve())
        }

        const resolveWhenEntitySyncComplete = () => {
          this.versionsStore
            .getEntityVersion(entityStoreName)
            .then((entityVersion: $TSFixMe) => {
              if (!entityVersion || entityVersion.initialSyncHasOccurred) {
                // Either not a versioned entity and we have no way of knowing whether initial sync has
                // occurred or not, or the entity has been synced, resolve immediately
                resolve()
              } else {
                // Entity hasn't had initial sync yet, wait for that to complete before resolving
                resolveWhenEntitySyncCompleteFired()
              }
            })
            .catch((error: $TSFixMe) => {
              $log.warn('[SyncManager] Error checking entity version', {
                error,
                entityStoreName,
              })
              resolveWhenEntitySyncCompleteFired()
            })
        }
        const syncConfig = this._currentEntitiesBeingSynced[entityStoreName]

        if (this.syncInProgress() && syncConfig) {
          // Entity is currently being synced, wait for the respective `entitySyncComplete` event
          syncConfig.promise.then(
            () => resolve(),
            resolveWhenEntitySyncComplete
          )
        } else {
          // Entity is not currently syncing, check if initial sync has occurred before resolving
          resolveWhenEntitySyncComplete()
        }
      })
    )
  }

  /**
   * Get notified when a minimal complete dataset is available. If we're online this will also wait for the
   * first sync of the page load to complete, otherwise simply ensures we have at least done a complete sync once
   * before.
   *
   * @method whenCompleteDatasetAvailable
   *
   * @return {Promise} A promise resolved when a minimal complete dataset is available
   */
  public whenCompleteDatasetAvailable() {
    // @todo display sync status somewhere

    return this._whenSyncStarted().then(() =>
      $q(resolve => {
        const resolveWhenInitialSyncComplete = () => {
          if (this.posStateManager.getInitialSyncComplete()) {
            resolve()
          } else {
            this.posStateManager.on('initialSyncCompleteChange', () =>
              resolve()
            )
          }
        }
        if (this.syncHasOccurred) {
          resolve()
        } else {
          if (this.syncInProgress()) {
            this._currentActiveSync.then(
              resolve,
              resolveWhenInitialSyncComplete
            )
          } else {
            resolveWhenInitialSyncComplete()
          }
        }
      })
    )
  }

  /**
   * Initialise the sync manager, binding events and initialising the delegate sync managers.
   *
   * @private
   * @method _init
   *
   * @return {SyncManager} The manager instance for chaining purposes
   */
  private _init() {
    // Track unload event during initial sync
    $window.addEventListener('beforeunload', this._trackInitialSyncUnload)

    this.posStateManager.on(
      'outletIdChange',
      this._onOutletIdChangeResync,
      this
    )
    this.webRegisterStatusManager.on(
      EVENTS.statusChange,
      this._onWrStatusChange,
      this
    )
    this._bindSyncManager(this.syncedEntityVersions)

    this._resyncOnOutletIdChange = this.syncedEntityVersions.getEntitiesToResyncOnOutletIdChange()

    this.vdFeatureManager.init()

    return this._prepareRetailerSettings().then(() => this)
  }

  /**
   * Track 'Init Sync Abort' event.
   * Triggered when abort/leave/close sell screen during initial sync.
   *
   * @private
   * @method _trackInitialSyncUnload
   */
  private _trackInitialSyncUnload = () => {
    if (!this.posStateManager.getInitialSyncComplete()) {
      const data = {
        moduleName: 'Web Register',
        eventName: 'Init Sync Abort',
        sendSynchronously: true,
        useSendBeacon: true,
      }
      this.vdTracking.trackUserEvent(data)
    }

    $window.removeEventListener('beforeunload', this._trackInitialSyncUnload)
  }

  /**
   * Check whether the retailer should be resynced.
   *
   * @private
   * @method _prepareRetailerSettings
   *
   * @return {Promise} Returns a promise that's resolved when retailer ready for resync.
   */
  private _prepareRetailerSettings() {
    return this.retailerSettingsStore
      .get()
      .then((retailerSettings: $TSFixMe) => {
        if (retailerSettings && !retailerSettings.terms_and_conditions) {
          return this.syncedEntityVersions.prepareEntityForResync(
            'retailerSettings'
          )
        }
      })
  }

  /**
   * Wait for the sync manager to have been initialised and the first sync started.
   *
   * @private
   * @method _whenSyncStarted
   *
   * @return {Promise} A promise resolved once the sync manager has been initialised and started syncing
   */
  private _whenSyncStarted() {
    return this.init().then(() => {
      if (
        !this.syncInProgress() &&
        !this._nextScheduledSync &&
        !this._whenSyncTriggered
      ) {
        this._whenSyncTriggered = $q.defer()
        return this._whenSyncTriggered.promise
      }
    })
  }

  /**
   * Bind events on the given sync manager.
   *
   * @private
   * @method _bindSyncManager
   *
   * @param manager {AbstractSyncManager}
   *        The sync manager to bind events on
   */
  private _bindSyncManager(manager: $TSFixMe) {
    manager.on('entitySyncTriggered', this._onEntitySyncTriggeredEvent, this)
    manager.on('entitySync', this._onEntitySyncEvent, this)
    manager.on('entitySyncComplete', this._onEntitySyncCompleteEvent, this)
    manager.on(
      'entitySyncPendingOutletId',
      this._onEntitySyncPendingOutletIdEvent,
      this
    )
  }

  /**
   * Handle an `entitySyncTriggered` event indicating an entity sync is starting. Tracks the given entity as currently
   * being synced.
   *
   * @private
   * @method _onEntitySyncTriggeredEvent
   *
   * @param entitySyncConfig
   *        The sync config for the entity being synced
   */
  private _onEntitySyncTriggeredEvent(entitySyncConfig: $TSFixMe) {
    const { entityStoreName } = entitySyncConfig
    this._currentEntitiesBeingSynced[entityStoreName] = entitySyncConfig

    entitySyncConfig.promise.catch(angular.noop).finally(() => {
      this._currentEntitiesBeingSynced[entityStoreName] = null
    })
    this.emit('entitySyncTriggered', entitySyncConfig)
    this.emit(`entitySyncTriggered:${entityStoreName}`, entitySyncConfig)
  }

  /**
   * Handle an `entitySync` event indicating an entity sync is starting.
   *
   * @private
   * @method _onEntitySyncEvent
   *
   * @param entitySyncConfig
   *        The sync config for the entity being synced
   */
  private _onEntitySyncEvent(entitySyncConfig: $TSFixMe) {
    const { entityStoreName } = entitySyncConfig
    this.emit('entitySync', entitySyncConfig)
    this.emit(`entitySync:${entityStoreName}`, entitySyncConfig)
  }

  /**
   * Handle an `entitySyncComplete` event indicating an entity sync has completed. Clears the given entity
   * from the list of currently syncing entities.
   *
   * @private
   * @method _onEntitySyncCompleteEvent
   *
   * @param entitySyncConfig
   *        The sync config for the entity that has completed sync
   */
  private _onEntitySyncCompleteEvent(entitySyncConfig: $TSFixMe) {
    const entityStoreName = entitySyncConfig.entityStoreName
    this.emit('entitySyncComplete', entitySyncConfig)
    this.emit('entitySyncComplete:' + entityStoreName, entitySyncConfig)
  }

  /**
   * Handle an `entitySyncPendingOutletId` event indicating an entity sync has been suspended until the user
   * chooses a register (and hence an outlet).
   *
   * @private
   * @method _onEntitySyncPendingOutletIdEvent
   *
   * @param entitySyncConfig
   *        The sync config for the entity pending the outlet ID
   */
  private _onEntitySyncPendingOutletIdEvent(entitySyncConfig: $TSFixMe) {
    this._entitiesPendingOutletId[entitySyncConfig.entityStoreName] =
      entitySyncConfig.outletId
  }

  /**
   * Calls the promotions endpoint and checks the count of those promotions currently active. If the request fails,
   * set the hasActivePromotions to true to safeguard against promotions actually being available. Pass an end time
   * otherwise past promotions will also be considered.
   *
   * @private
   * @method _checkForActivePromotions
   *
   * @return {Promise} Resolved once the $http promotions request has completed.
   */
  private _checkForActivePromotions() {
    const activeOutlet = this.registerManager.getActiveOutlet()
    const endTimeFrom = moment
      .tz(activeOutlet.time_zone)
      .format(PROMOTIONS.DATE_FORMAT)
    // @todo Also use start_time when the param is supported by the api to filter out future promotions
    return $http
      .get<$TSFixMe>(`${PROMOTIONS_ENDPOINT}?end_time_from=${endTimeFrom}`)
      .then(({ data }) => {
        const promotions = data && data.data
        this.hasActivePromotions = Boolean(promotions && promotions.length)

        // Store latest hasActivePromotions result in local storage
        saveToStorage(HAS_ACTIVE_PROMOTIONS, this.hasActivePromotions)
      })
      .catch(() => {
        $log.warn('[SyncManager] Failed to retrieve promotions')
        this.hasActivePromotions = true
      })
  }

  /**
   * Executes the sync by delegating to the various sync managers.
   *
   * @private
   *
   * @method _executeSync
   *
   * @param {Object} options
   *        @param {Boolean} [options.skipEmitSyncComplete]
   *               Optional boolean true when we need to skip the emit of the sync complete event, false otherwise
   *        @param {Boolean} [options.isFirstSync]
   *               Optional boolean true when sync is triggered from sell screen reload, false/undefined otherwise
   *
   * @return {Promise} A promise resolved once the sync has completed
   */
  private _executeSync(
    options: {
      skipEmitSyncComplete?: boolean
      isFirstSync?: boolean
    } = {}
  ) {
    const isInitialSync = !this.posStateManager.getInitialSyncComplete()
    const isWarmBoot = options.isFirstSync && !isInitialSync

    let timer: Timer | null = null
    if (!this.webRegisterStatusManager.isOnline()) {
      this._currentActiveSync = $q.reject(
        new SyncInterruptedError('Application is offline')
      )
    } else if (isWarmBoot) {
      // Hold off sync when warm boot
      this._currentActiveSync = $q.resolve()
    } else {
      // Reset API requests counting then set a mark to track sync performance data
      this.apiIdentification.startCountingRequests()
      this.vdPerformanceTracking.start(SYNC_METRIC)
      timer = StartTimer('webregister.sync_timer')
      this._currentActiveSync = this.syncedEntityVersions
        .sync()
        .then((syncResults: $TSFixMe) => {
          return this._checkForActivePromotions()
            .then(() => this._setSyncCompleteFlags(syncResults))
            .then(() => {
              // Set stop mark for sync performance data
              const syncMeasure = this.vdPerformanceTracking.stop(SYNC_METRIC)
              const apiCount = this.apiIdentification.stopCountingRequests()

              this.tagAndCaptureSyncMetrics(timer, options.isFirstSync, false)
              this._trackSyncMetrics(apiCount, syncMeasure, isInitialSync)
              return syncResults
            })
        })
        .then((syncResults: $TSFixMe) => {
          if (options.skipEmitSyncComplete) {
            return $q.resolve()
          }
          return this._emitSyncComplete(syncResults)
        })
    }
    if (this._whenSyncTriggered) {
      this._whenSyncTriggered.resolve()
    }
    return this._currentActiveSync
      .catch((error: $TSFixMe) =>
        this._handleSyncError(error, timer, options.isFirstSync)
      )
      .finally(() => this._finaliseSync({ isWarmBoot }))
  }

  private async tagAndCaptureSyncMetrics(
    timer: Timer | null,
    isFirstSync?: boolean,
    hasError?: boolean
  ) {
    if (!timer) {
      return
    }

    timer.tag('page_size', '2000')

    if (isFirstSync) {
      timer.tag('is_first_sync', 'true')
    }

    const productCount = await this.productsStore.count()
    timer.tag('product_count', getProductCountBucket(productCount))

    const retailerID = (await this.retailerSettingsStore.get()).id
    const group = getRetailerGroupForSyncMetrics(retailerID)
    if (group) {
      timer.tag('retailer_group', group)
    }

    if (hasError) {
      timer.tag('error', 'true')
    }

    timer.stop()
  }

  /**
   * Track Sync metrics (e.g. time to sync data, api requests number, etc) by sending
   * a 'Sync' event containing the relevant information.
   * All time arguments are in milliseconds since epoch.
   *
   * @private
   * @method _trackSyncMetrics
   *
   * @param {Number} apiRequestCount
   *        The number of api requests during sync period
   *
   * @param {Object} syncMeasure
   *        The performance tracking info returned by vdPerformanceTracking.stop(key)
   *
   * @param {Boolean} isInitialSync
   *        If it's the initial Sync
   */
  private _trackSyncMetrics(
    apiRequestCount: number,
    syncMeasure: any = {},
    isInitialSync: boolean
  ) {
    this.vdTracking.trackUserEvent({
      eventName: SYNC_METRIC,
      metadata: {
        apiRequestCount,
        isInitialSync,
        syncStartTime: syncMeasure.startTime,
        syncDuration: syncMeasure.duration,
      },
    })
  }

  /**
   * Abort the current active sync by interrupting all entities currently being synced.
   *
   * @private
   * @method _abortSync
   */
  private _abortSync() {
    each(this._currentEntitiesBeingSynced, entitySyncConfig =>
      this._interruptEntitySync(entitySyncConfig)
    )
  }

  /**
   * Cancel the next timed poll. If a sync is running or triggered manually the poll will be reinitiated.
   *
   * @private
   * @method _cancelNextPoll
   */
  private _cancelNextPoll() {
    if (this._nextSyncPoll) {
      this._nextSyncPoll.cancel()
      this._nextSyncPoll = null
      if (this._nextScheduledSync) {
        // We reject the promise in case anything is listening for it,
        // but we also need to catch the rejection to avoid creating
        // an unhandled rejection error in the console and error tracking
        this._nextScheduledSync.promise.catch(() => {})
        this._nextScheduledSync.reject('cancelled')
        this._nextScheduledSync = null
      }
    }
  }

  /**
   * Handles errors that occur during sync.
   *
   * @private
   * @method _handleSyncError
   *
   * @param error {Any}
   *        The error that was thrown during sync
   */
  private _handleSyncError(
    error: any,
    timer: Timer | null,
    isFirstSync?: boolean
  ) {
    if (error && !(error instanceof SyncInterruptedError)) {
      $log.warn('[SyncManager] SYNC FAILED', error)
    }

    this.tagAndCaptureSyncMetrics(timer, isFirstSync, true)
    return $q.reject(error)
  }

  /**
   * Set the `syncHasOccurred` flag and `initialSyncComplete` property in the POS state manager after the sync
   * has completed.
   *
   * @private
   * @method _setSyncCompleteFlags
   *
   * @param syncResults {Object}
   *        The data from the sync
   *
   * @return {Object} Pass on the same data received so that the promise chain is resolved with it
   */
  private _setSyncCompleteFlags(syncResults: any) {
    this.syncHasOccurred = true
    this.posStateManager.setInitialSyncComplete(true)
    return syncResults
  }

  /**
   * Emits the `syncComplete` event on the syncManager instance and the $rootScope.
   *
   * @private
   * @method _emitSyncComplete
   *
   * @param {Object} syncResults
   *        The data from the sync
   *
   * @return {Object} Pass on the same data received so that the promise chain is resolved with it
   */
  private _emitSyncComplete(syncResults: any) {
    try {
      this.emit('syncComplete', syncResults)
    } catch (e) {
      $log.warn('Error thrown handling `syncComplete` event', e)
    }

    return syncResults
  }

  /**
   * Finalises the sync, resetting any internal state and scheduling the next sync.
   *
   * @private
   * @param {Object} options
   *        @param {Boolean} [options.isWarmBoot]
   *               Optional boolean true when warm boot, false/undefined otherwise
   *
   * @method _finaliseSync
   */
  private _finaliseSync(
    options: {
      isWarmBoot?: boolean
    } = {}
  ) {
    this._currentActiveSync = null
    if (!this._nextScheduledSync) {
      if (!this.syncHasOccurred && options.isWarmBoot) {
        // Set a quick scheduled sync after 5-secs when have avoided sync during warm boot
        this._scheduleNextSyncPoll(5000)
      } else {
        // Set a default 5-mins periodic sync
        this._scheduleNextSyncPoll()
      }
    }
  }

  /**
   * Triggers the next scheduled sync (either by timeout since the last sync, or manually triggered during an
   * existing sync).
   *
   * @private
   * @method _executeNextScheduledSync
   *
   * @return {Promise} A promise resolved once the triggered sync has completed
   */
  private _executeNextScheduledSync() {
    const nextSyncDeferred = this._nextScheduledSync
    this._nextScheduledSync = null
    this._nextSyncPoll = null

    const activeSyncPromise = this._executeSync()

    nextSyncDeferred.resolve(activeSyncPromise)

    return activeSyncPromise
  }

  /**
   * Schedule the next sync after a 5ish minutes wait.
   *
   * @private
   * @method _scheduleNextSyncPoll
   *
   * @param  [timeout] {Number}
   *         Optional custom timeout to schedule the next sync after
   */
  private _scheduleNextSyncPoll(timeout: number = syncTimeout) {
    const executeNextSync = this._executeNextScheduledSync.bind(this)
    // We don't use $timeout as protractor waits for any $timeout's to clear before taking each step causing the
    // tests to take forever to run
    const timeoutId = $window.setTimeout(executeNextSync, timeout)
    this._nextScheduledSync = $q.defer()
    this._nextSyncPoll = {
      cancel() {
        $window.clearTimeout(timeoutId)
      },
      // Flush the timer instantly - exposed for unit testing purposes
      __flush() {
        this.cancel()
        executeNextSync()
      },
    }
  }

  /**
   * Interrupt the current sync of the given entity.
   *
   * @private
   * @method _interruptEntitySync
   *
   * @param  entity {String|EntitySyncConfig}
   *         Either the EntitySyncConfig or the entity store name or the entity to interrupt
   */
  private _interruptEntitySync(entity: string | $TSFixMe) {
    const entitySyncConfig = angular.isString(entity)
      ? this._currentEntitiesBeingSynced[entity]
      : entity

    if (entitySyncConfig) {
      entitySyncConfig.interrupt = true
    }
  }

  /**
   * Handle the outlet ID changing by resuming any paused syncs that were waiting for it or triggering a
   * re-sync for those entities that rely on it.
   *
   * @private
   * @method _onOutletIdChangeResync
   *
   * @param event {Event}
   *        Data from the change event
   */
  private _onOutletIdChangeResync(event: { newValue?: string }) {
    const outletId = event.newValue
    let entitiesToClearAndResync

    if (this.syncInProgress()) {
      entitiesToClearAndResync = filter(
        this._resyncOnOutletIdChange,
        entityStoreName => {
          const pendingSync = this._entitiesPendingOutletId[entityStoreName]

          if (pendingSync) {
            pendingSync.resolve(outletId)
            delete this._entitiesPendingOutletId[entityStoreName]

            return false
          } else {
            this._interruptEntitySync(entityStoreName)
            return true
          }
        }
      )
    } else {
      entitiesToClearAndResync = this._resyncOnOutletIdChange
    }

    if (entitiesToClearAndResync.length) {
      $q.all(
        map(entitiesToClearAndResync, entityStoreName =>
          this.syncedEntityVersions.prepareEntityForResync(entityStoreName)
        )
      ).then(() => this.sync())
    }
  }

  /**
   * Handle the online/offline status of webregister changing. Aborts any active sync if transitioning offline or
   * schedules the next sync if transitioning online.
   *
   * @private
   * @method _onWrStatusChange
   */
  private _onWrStatusChange() {
    if (this.webRegisterStatusManager.isOnline()) {
      if (!this._nextScheduledSync) {
        // Schedule a sync some time in the next minute or so
        this._scheduleNextSyncPoll(jitterTime(1, 0.5))
      }
    } else if (this.syncInProgress()) {
      this._abortSync()
    }
  }
}

/**
 * Responsible for syncing data with the server via the /api/2.0 end points.
 *
 * @module syncManager
 */
angular.module('webregister.managers').factory('syncManager', SyncManager)
